import React, { useState } from "react";
import { Mail, AlertCircle, Send } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success">("idle");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("success");
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Lo-Fee.com</title>
        <meta
          name="description"
          content="Have questions about Canadian Bitcoin exchange fees? Contact the Lo-Fee.com team for help with our cryptocurrency exchange comparison tool."
        />
        <link rel="canonical" href="https://lo-fee.com/contact" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">Contact Us</h1>
            <p className="text-lg text-[--text-secondary]">
              Have questions or feedback? We'd love to hear from you.
            </p>
          </header>

          <div className="grid md:grid-cols-2 gap-12">
            <section className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-2xl font-bold">Get in Touch</h2>
                <p className="text-[--text-secondary]">
                  We aim to respond to all inquiries within 24 hours during
                  business days.
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href="mailto:contact@lo-fee.com"
                  className="flex items-start gap-4 p-6 bg-[--bg-secondary] border-2 border-[--border-color] hover:border-[--accent] transition-colors"
                >
                  <Mail className="h-6 w-6 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-lg">Email Us</h3>
                    <p className="text-[--text-secondary] mt-1">
                      contact@lo-fee.com
                    </p>
                    <p className="text-sm text-[--text-secondary] mt-2">
                      For general inquiries, support, and feedback
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-amber-900">Response Time</h3>
                    <p className="text-amber-800 mt-1">
                      We typically respond within 24 hours during business days.
                      For urgent matters, please include "URGENT" in your
                      subject line.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-2xl font-bold">Send a Message</h2>
                <p className="text-[--text-secondary]">
                  Use this form to send us a direct message. We'll get back to
                  you as soon as possible.
                </p>
              </div>

              {status === "success" ? (
                <div className="bg-green-50 border-2 border-green-200 p-6 rounded-lg">
                  <div className="flex items-center gap-3 text-green-800">
                    <Send className="h-5 w-5" />
                    <div>
                      <h3 className="font-bold">Message Sent!</h3>
                      <p className="text-sm mt-1">
                        Thank you for your message. We'll respond shortly.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="block font-bold">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      className="w-full px-4 py-2 bg-[--bg-secondary] border-2 border-[--border-color] focus:border-[--accent]"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="block font-bold">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      className="w-full px-4 py-2 bg-[--bg-secondary] border-2 border-[--border-color] focus:border-[--accent]"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="subject" className="block font-bold">
                      Subject
                    </label>
                    <select
                      id="subject"
                      required
                      className="w-full px-4 py-2 bg-[--bg-secondary] border-2 border-[--border-color] focus:border-[--accent]"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="feedback">Feedback</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block font-bold">
                      Message
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={5}
                      className="w-full px-4 py-2 bg-[--bg-secondary] border-2 border-[--border-color] focus:border-[--accent]"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="px-6 py-2 bg-[--accent] text-white font-bold hover:bg-[--accent]/90 transition-colors"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
